import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import { Product, DepositProduct } from '../types/Product';
import { ProductApi, DepositApi, TransactionApi, PersonalCabinetApi } from '../api';
import { ProductTransferApi } from '../api/ProductTransferApi';
import type { PersonalDeposit } from '../api/PersonalCabinetApi';
import { isLowStock } from '../utils/stock';

type Company = { id: number; name: string; location?: string };

export default function InventoryManagePage() {
  const { t } = useTranslation();
  const { isActive: subscriptionActive } = useSubscriptionStatus();
  const [products, setProducts] = useState<Product[]>([]);
  const [deposits, setDeposits] = useState<PersonalDeposit[]>([]);
  const [depositProducts, setDepositProducts] = useState<DepositProduct[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<number | null>(null);
  const [selectedDeposit, setSelectedDeposit] = useState<number | null>(null);
  const [scanValue, setScanValue] = useState('');
  const [manualProductId, setManualProductId] = useState<number | null>(null);
  const [manualEntryOpen, setManualEntryOpen] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [lowStockThreshold, setLowStockThreshold] = useState(() => {
    const saved = Number(localStorage.getItem('lowStockThreshold'));
    return Number.isInteger(saved) && saved >= 0 ? saved : 5;
  });
  const [showLowStockDetails, setShowLowStockDetails] = useState(false);
  const [bulkAddMode, setBulkAddMode] = useState(false);
  const [bulkRemoveMode, setBulkRemoveMode] = useState(false);
  const [bulkCount, setBulkCount] = useState(0);
  const [bulkSellItems, setBulkSellItems] = useState<Array<{productId: number; productName: string; price: number; originalPrice: number; percentage: number}>>([]);
  const [salePercentage, setSalePercentage] = useState<number>(100);
  const [deliveryCost, setDeliveryCost] = useState<string>('');
  const [adsCost, setAdsCost] = useState<string>('');
  const [showBulkSellStats, setShowBulkSellStats] = useState(false);
  const scanInputRef = useRef<HTMLInputElement>(null);

  // Filter states
  const [searchFilter, setSearchFilter] = useState('');
  const [firmFilter, setFirmFilter] = useState('');
  const [modelFilter, setModelFilter] = useState('');
  const [sizeFilter, setSizeFilter] = useState('');

  const matchesScannedCode = (product: Product, scannedValue: string) => {
    const code = scannedValue.trim();
    if (!code) return false;

    const candidates = [
      product.customBarcode,
      product.customQrCode,
      product.barcode,
      product.qrCode,
    ].filter((value): value is string => Boolean(value && value.trim()));

    return candidates.some(candidate => candidate.trim() === code);
  };

  const getProductDisplayName = (product: Product) => {
    const details = [product.name, product.firm, product.model, product.size]
      .map(value => value?.trim())
      .filter((value): value is string => Boolean(value));
    return details.join(' · ') || t('inventory.unknownProduct');
  };

  const resetProductEntry = () => {
    setScanValue('');
    setManualProductId(null);
    window.setTimeout(() => scanInputRef.current?.focus(), 0);
  };

  useEffect(() => {
    fetchData();
    // Initial data should only be loaded when this page is mounted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedDeposit) {
      console.log(`[InventoryManagePage] Deposit selection changed to: ${selectedDeposit}`);
      // Clear previous deposit's data when switching deposits
      setDepositProducts([]);
      fetchDepositProducts(selectedDeposit);
    } else {
      console.log('[InventoryManagePage] No deposit selected, clearing data');
      // Clear data when no deposit is selected
      setDepositProducts([]);
    }
    // The selected deposit is the only trigger; the loader itself is not state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDeposit]);

  useEffect(() => {
    if (selectedCompany) {
      fetchProductsForCompany(selectedCompany);
    }
  }, [selectedCompany]);

  useEffect(() => {
    if (!selectedCompany) {
      setSelectedDeposit(null);
      return;
    }

    const companyDeposits = deposits.filter(
      deposit => deposit.company && deposit.company.id === selectedCompany
    );

    if (companyDeposits.length === 0) {
      setSelectedDeposit(null);
      return;
    }

    const isCurrentDepositValid = companyDeposits.some(
      deposit => deposit.id === selectedDeposit
    );

    if (!isCurrentDepositValid) {
      setSelectedDeposit(companyDeposits[0].id);
    }
  }, [selectedCompany, deposits, selectedDeposit]);

  useEffect(() => {
    // Focus input when entering bulk mode; do NOT set up an interval —
    // focus is returned to the scan input only after each successful scan (Enter).
    setScanValue('');
    setManualProductId(null);
    if ((bulkAddMode || bulkRemoveMode) && scanInputRef.current) {
      scanInputRef.current.focus();
    }
  }, [bulkAddMode, bulkRemoveMode]);

  const fetchData = async () => {
    try {
      const [depositsRes, companiesRes] = await Promise.all([
        PersonalCabinetApi.getPersonalDeposits(),
        PersonalCabinetApi.getPersonalCompanies()
      ]);
      setDeposits(depositsRes.data);
      setCompanies(companiesRes.data);
      
      if (depositsRes.data.length > 0) {
        setSelectedDeposit(depositsRes.data[0].id);
      }
      
      // Set first company as default
      if (companiesRes.data.length > 0) {
        setSelectedCompany(companiesRes.data[0].id);
        // Fetch products for the first company
        fetchProductsForCompany(companiesRes.data[0].id);
      }
    } catch (err) {
      setError(t('inventory.failedToLoadData'));
    }
  };

  const fetchProductsForCompany = async (companyId: number) => {
    try {
      // Fetch both visible and invisible products for scanning
      const [visibleResponse, allResponse] = await Promise.all([
        ProductApi.getByCompany(companyId),
        ProductApi.getByCompany(companyId, true).catch(() => ({ data: [] }))
      ]);
      
      // Combine visible and invisible products, removing duplicates
      const visibleProducts = visibleResponse.data || [];
      const allProducts = allResponse.data || [];
      
      // Create a combined list, prioritizing visible products
      const productMap = new Map<number, Product>();
      visibleProducts.forEach((p: Product) => productMap.set(p.id!, p));
      allProducts.forEach((p: Product) => {
        if (!productMap.has(p.id!)) {
          productMap.set(p.id!, { ...p, isDeleted: true } as Product);
        }
      });
      
      const combinedProducts = Array.from(productMap.values());
      setProducts(combinedProducts);
      
      console.log('Loaded products for company:', {
        companyId,
        visible: visibleProducts.length,
        total: combinedProducts.length,
        deleted: combinedProducts.filter((p: any) => p.isDeleted).length
      });
    } catch (err) {
      console.error('Failed to fetch products for company:', err);
      setProducts([]);
    }
  };

  const fetchDepositProducts = async (depositId: number) => {
    try {
      console.log(`[InventoryManagePage] Fetching products for deposit ID: ${depositId}`);
      const response = await DepositApi.getDepositProducts(depositId);
      console.log(`[InventoryManagePage] Received ${response.data.length} products for deposit ${depositId}:`, response.data);
      setDepositProducts(response.data);
    } catch (err) {
      console.error(`[InventoryManagePage] Failed to load products for deposit ${depositId}:`, err);
      setError(t('inventory.failedToLoadDepositProducts'));
    }
  };

  const refreshAllData = async () => {
    // Refresh company products if a company is selected
    if (selectedCompany) {
      await fetchProductsForCompany(selectedCompany);
    }
    
    // Refresh deposit products if a deposit is selected
    if (selectedDeposit) {
      await fetchDepositProducts(selectedDeposit);
    }
    
    // Refresh deposits and companies data
    try {
      const [depositsRes, companiesRes] = await Promise.all([
        PersonalCabinetApi.getPersonalDeposits(),
        PersonalCabinetApi.getPersonalCompanies()
      ]);
      setDeposits(depositsRes.data);
      setCompanies(companiesRes.data);
    } catch (err) {
      console.error('Failed to refresh basic data:', err);
    }
  };

  const toggleBulkAddMode = async () => {
    if (bulkAddMode) {
      // Exiting bulk add mode
      setBulkAddMode(false);
      setManualEntryOpen(false);
      const finalCount = bulkCount;
      setBulkCount(0);
      setSuccess(t('inventory.bulkAddModeEnded', { count: finalCount }));
      
      // Refresh all data after bulk operations
      if (finalCount > 0) {
        await refreshAllData();
        setSuccess(t('inventory.bulkAddModeEnded', { count: finalCount }) + ' - ' + (t('inventory.pageRefreshed') || 'Page refreshed'));
      }
    } else {
      // Entering bulk add mode
      setBulkAddMode(true);
      setBulkRemoveMode(false);
      setManualEntryOpen(true);
      setBulkCount(0);
      setSuccess(t('inventory.bulkAddModeActivated'));
      // Ensure focus stays on input
      setTimeout(() => {
        if (scanInputRef.current) {
          scanInputRef.current.focus();
        }
      }, 200);
    }
  };

  const toggleBulkRemoveMode = async () => {
    if (bulkRemoveMode) {
      // Exiting bulk remove mode
      setBulkRemoveMode(false);
      setManualEntryOpen(false);
      const finalCount = bulkCount;
      setBulkCount(0);
      if (finalCount > 0) {
        setShowBulkSellStats(true);
        await refreshAllData();
      } else {
        setSuccess(t('inventory.bulkRemoveModeEnded', { count: finalCount }));
      }
    } else {
      // Entering bulk remove mode
      setBulkRemoveMode(true);
      setBulkAddMode(false);
      setManualEntryOpen(true);
      setBulkCount(0);
      setBulkSellItems([]);
      setDeliveryCost('');
      setAdsCost('');
      setSuccess(t('inventory.bulkRemoveModeActivated'));
      // Ensure focus stays on input
      setTimeout(() => {
        if (scanInputRef.current) {
          scanInputRef.current.focus();
        }
      }, 200);
    }
  };

  const handleScanAdd = async (manuallySelectedProduct?: Product) => {
    if (!selectedDeposit) {
      setError(t('inventory.selectDepositFirst'));
      resetProductEntry();
      return;
    }
    
    if (!selectedCompany) {
      setError(t('inventory.selectCompanyFirst'));
      resetProductEntry();
      return;
    }

    setError('');
    setSuccess('');
    const value = scanValue.trim();
    if (!manuallySelectedProduct && !value) {
      setError(t('inventory.enterOrSelectProduct'));
      resetProductEntry();
      return;
    }

    setLoading(true);
    try {
      let product: Product | undefined = manuallySelectedProduct;
      if (!product) {
        try {
          const resolved = (await ProductApi.scan(value)).data;
          product = products.find(candidate => candidate.id === resolved.id);
        } catch {
          // A local exact-code fallback keeps already loaded stock usable during a brief network error.
          product = products.find(candidate => matchesScannedCode(candidate, value));
        }
      }

      if (!product || !product.id) {
        setError(t('inventory.productNotFound', { productCode: value }));
        return;
      }

      let receivedFromTransit = false;
      try {
        await ProductTransferApi.receiveMatching(selectedDeposit, product.id!);
        receivedFromTransit = true;
      } catch (transferError: any) {
        if (transferError?.response?.status !== 404) throw transferError;
        await DepositApi.addProductToDeposit(selectedDeposit, product.id!, 1);
      }
      
      // Log transaction for adding stock (company-based)
      if (!receivedFromTransit) try {
        console.log('Attempting to log add-stock transaction with data:', {
          productId: product.id,
          companyId: selectedCompany,
          quantity: 1,
          notes: `Added via inventory management - ${product.name}`
        });
        
        const transactionResult = await TransactionApi.addStock(product.id!, {
          companyId: selectedCompany!,
          depositId: selectedDeposit!,
          quantity: 1,
          notes: `Added via inventory management - ${product.name}`
        });
        
        console.log('Add-stock transaction logged successfully:', transactionResult.data);
      } catch (transactionError: any) {
        console.error('Failed to log add-stock transaction:', transactionError);
        console.error('Add-stock transaction error details:', transactionError.response?.data);
        // Don't fail the whole operation if transaction logging fails
      }
      
      if (bulkAddMode) {
        setBulkCount(prev => prev + 1);
        setSuccess(t('inventory.bulkAdded', { productName: product.name || t('inventory.unknownProduct'), count: bulkCount + 1 }));
      } else {
        setSuccess(t('inventory.addedToDeposit', { productName: product.name || t('inventory.unknownProduct') }));
      }
      
      // Force immediate refresh of all data
      await Promise.all([
        fetchDepositProducts(selectedDeposit),
        selectedCompany ? fetchProductsForCompany(selectedCompany) : Promise.resolve()
      ]);
    } catch (err: any) {
      setError(err.response?.data || t('inventory.failedToAddProduct'));
    } finally {
      setLoading(false);
      resetProductEntry();
    }
  };

  const handleScanRemove = async (manuallySelectedProduct?: Product) => {
    if (!selectedDeposit) {
      setError(t('inventory.selectDepositFirst'));
      resetProductEntry();
      return;
    }
    
    if (!selectedCompany) {
      setError(t('inventory.selectCompanyFirst'));
      resetProductEntry();
      return;
    }

    setError('');
    setSuccess('');
    const value = scanValue.trim();
    if (!manuallySelectedProduct && !value) {
      setError(t('inventory.enterOrSelectProduct'));
      resetProductEntry();
      return;
    }

    // Parse scanned value - could be JSON QR code, Product ID (numeric or EAN-13) or firm-model-size format
    let product: Product | undefined = manuallySelectedProduct;
    let resolvedProductId: number | null = null;

    // If it's a JSON QR code (e.g. {"id":42,"firm":"Nike",...}), extract the id
    if (!product && value.startsWith('{')) {
      try {
        const parsed = JSON.parse(value);
        if (parsed.id) resolvedProductId = Number(parsed.id);
      } catch { /* not valid JSON, fall through */ }
    }

    // Check if it's a 13-digit EAN-13 barcode (Product ID with check digit)
    if (!product && resolvedProductId === null && /^\d{13}$/.test(value)) {
      const productIdStr = value.substring(0, 12);
      resolvedProductId = parseInt(productIdStr, 10);
      console.log('Scanning EAN-13 for removal:', { ean13: value, extractedId: resolvedProductId });
    }
    // Check if it's a numeric Product ID
    else if (!product && resolvedProductId === null && /^\d+$/.test(value)) {
      resolvedProductId = parseInt(value, 10);
      console.log('Scanning numeric ID for removal:', resolvedProductId);
    }

    if (resolvedProductId !== null) {
      product = products.find(p => p.id === resolvedProductId);
      if (product) console.log('Found product by ID for removal:', product.id, product.name);
    }

    // Otherwise, try exact custom/derived barcode/QR match.
    if (!product) {
      product = products.find(p => matchesScannedCode(p, value));
    }

    // If still not found, treat as firm-model-size format
    if (!product) {
      const parts = value.split('-');
      if (parts.length < 3) {
        setError(t('inventory.formatError'));
        resetProductEntry();
        return;
      }

      const [firm, model, size] = parts.map(s => s.trim().toLowerCase());
      
      console.log('Scanning firm-model-size for removal:', { firm, model, size });
      console.log('Available products for removal:', products.map(p => ({
        name: p.name,
        firm: p.firm,
        model: p.model,
        size: p.size,
        id: p.id
      })));
      
      product = products.find(p => {
        const pFirm = (p.firm || '').trim().toLowerCase();
        const pModel = (p.model || '').trim().toLowerCase();
        const pSize = (p.size || '').trim().toLowerCase();
        
        console.log('Comparing with product for removal:', {
          name: p.name,
          pFirm,
          pModel,
          pSize,
          matches: {
            firm: pFirm === firm,
            model: pModel === model,
            size: pSize === size
          }
        });
        
        return pFirm === firm && pModel === model && pSize === size;
      });
    }

    if (!product || !product.id) {
      setError(t('inventory.productNotFoundForRemoval', { productCode: value }));
      resetProductEntry();
      return;
    }

    // Capture as const — TypeScript doesn't narrow `let` through async boundaries.
    // product.id is guaranteed non-null by the guard above.
    const resolvedProduct = product;
    const productId: number = resolvedProduct.id!;

    setLoading(true);
    try {
      if (bulkRemoveMode) {
        // Bulk sell – sell at the configured percentage of product price
        const originalPrice = resolvedProduct.price || 0;
        const actualPrice = parseFloat((originalPrice * (salePercentage / 100)).toFixed(2));

        try {
          await TransactionApi.recordSale(productId, {
            companyId: selectedCompany!,
            depositId: selectedDeposit!,
            quantity: 1,
            unitPrice: actualPrice,
            notes: `Sold via inventory management - ${resolvedProduct.name} (${salePercentage}% of ${originalPrice})`
          });
        } catch (transactionError: any) {
          console.error('Failed to log sale transaction:', transactionError);
        }

        await DepositApi.decreaseProductFromDeposit(selectedDeposit!, productId, 1);

        const newCount = bulkCount + 1;
        setBulkCount(newCount);
        setBulkSellItems(prev => [...prev, {
          productId: productId,
          productName: getProductDisplayName(resolvedProduct),
          price: actualPrice,
          originalPrice,
          percentage: salePercentage
        }]);
        setSuccess(t('inventory.bulkSold', {
          productName: getProductDisplayName(resolvedProduct),
          count: newCount,
          price: actualPrice.toFixed(2)
        }));

        await Promise.all([
          fetchDepositProducts(selectedDeposit!),
          selectedCompany ? fetchProductsForCompany(selectedCompany) : Promise.resolve()
        ]);
      } else {
        // Normal remove mode
        try {
          await TransactionApi.removeStock(productId, {
            companyId: selectedCompany!,
            depositId: selectedDeposit!,
            quantity: 1,
            notes: `Removed via inventory management - ${resolvedProduct.name}`
          });
        } catch (transactionError: any) {
          console.error('Failed to log transaction:', transactionError);
        }

        await DepositApi.decreaseProductFromDeposit(selectedDeposit, productId, 1);
        setSuccess(t('inventory.removedFromDeposit', { productName: resolvedProduct.name || t('inventory.unknownProduct') }));

        await Promise.all([
          fetchDepositProducts(selectedDeposit),
          selectedCompany ? fetchProductsForCompany(selectedCompany) : Promise.resolve()
        ]);
      }
    } catch (err: any) {
      setError(err.response?.data || t('inventory.failedToRemoveProduct'));
    } finally {
      setLoading(false);
      resetProductEntry();
    }
  };

  const handleQuantityChange = async (depositProduct: DepositProduct, newQuantity: number) => {
    setLoading(true);
    try {
      await DepositApi.setProductQuantityInDeposit(
        selectedDeposit!,
        depositProduct.product.id!,
        newQuantity
      );
      setSuccess(`Updated ${depositProduct.product.name || 'Unknown Product'} quantity to ${newQuantity}`);
      // Force immediate refresh of all data
      await Promise.all([
        fetchDepositProducts(selectedDeposit!),
        selectedCompany ? fetchProductsForCompany(selectedCompany) : Promise.resolve()
      ]);
    } catch (err: any) {
      setError(err.response?.data || 'Failed to update quantity');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromDeposit = async (depositProduct: DepositProduct) => {
    if (!window.confirm(`Remove all ${depositProduct.product.name || 'Unknown Product'} from this deposit?`)) return;
    
    setLoading(true);
    try {
      await DepositApi.setProductQuantityInDeposit(
        selectedDeposit!,
        depositProduct.product.id!,
        0
      );
      setSuccess(`Removed all ${depositProduct.product.name || 'Unknown Product'} from deposit`);
      // Force immediate refresh of all data
      await Promise.all([
        fetchDepositProducts(selectedDeposit!),
        selectedCompany ? fetchProductsForCompany(selectedCompany) : Promise.resolve()
      ]);
    } catch (err: any) {
      setError(err.response?.data || 'Failed to remove product');
    } finally {
      setLoading(false);
    }
  };

  const getSelectedDeposit = () => {
    return deposits.find(d => d.id === selectedDeposit);
  };

  // Filter deposit products based on search criteria
  const filteredDepositProducts = depositProducts.filter(dp => {
    const product = dp.product;
    const matchesSearch = !searchFilter || 
      (product.name && product.name.toLowerCase().includes(searchFilter.toLowerCase())) ||
      (product.description && product.description.toLowerCase().includes(searchFilter.toLowerCase()));
    const matchesFirm = !firmFilter || 
      (product.firm && product.firm.toLowerCase().includes(firmFilter.toLowerCase()));
    const matchesModel = !modelFilter || 
      (product.model && product.model.toLowerCase().includes(modelFilter.toLowerCase()));
    const matchesSize = !sizeFilter || 
      (product.size && product.size.toLowerCase().includes(sizeFilter.toLowerCase()));

    return matchesSearch && matchesFirm && matchesModel && matchesSize;
  });
  const lowStockProducts = depositProducts.filter(dp => isLowStock(dp.quantity, lowStockThreshold));

  // Suggestions for the editable filters. Users can type any partial value or
  // pick one of the existing values offered by the browser's datalist.
  const uniqueFirms = Array.from(new Set(depositProducts.map(dp => dp.product.firm).filter((value): value is string => Boolean(value)))).sort();
  const uniqueModels = Array.from(new Set(depositProducts.map(dp => dp.product.model).filter((value): value is string => Boolean(value)))).sort();
  const uniqueSizes = Array.from(new Set(depositProducts.map(dp => dp.product.size).filter((value): value is string => Boolean(value)))).sort();

  // Filter deposits to show only those associated with the selected company
  const filteredDeposits = selectedCompany 
    ? deposits.filter(deposit => {
        // Check if deposit has company association through the company property
        return deposit.company && deposit.company.id === selectedCompany;
      })
    : [];
  const manuallySelectedProduct = manualProductId == null
    ? undefined
    : products.find(product => product.id === manualProductId);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', background: '#fff', borderRadius: 12, boxShadow: '0 2px 16px #0001', padding: 32 }}>
      <h2 style={{ color: '#1976d2', marginBottom: 24 }}>{t('inventory.title')}</h2>

      {!subscriptionActive && (
        <div style={{
          background: '#fff3e0', border: '1px solid #ff9800', borderRadius: 8,
          padding: '12px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <span style={{ fontSize: 20 }}>⚠️</span>
          <span style={{ color: '#e65100', fontWeight: 600 }}>
            Your subscription has expired or is inactive. Inventory changes are disabled. Please renew your subscription.
          </span>
        </div>
      )}
      
      {/* Company Selection */}
      <div style={{ marginBottom: 24 }}>
        <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#1976d2' }}>
          {t('inventory.selectCompany')}
        </label>
        <select
          value={selectedCompany || ''}
          onChange={(e) => {
            const nextCompanyId = e.target.value ? Number(e.target.value) : null;
            setSelectedCompany(nextCompanyId);
          }}
          style={{ 
            padding: '8px 12px', 
            borderRadius: 6, 
            border: '1px solid #1976d2', 
            fontSize: 14,
            minWidth: 200
          }}
        >
          <option value="">Select a company...</option>
          {companies.map(company => (
            <option key={company.id} value={company.id}>
              {company.name}
            </option>
          ))}
        </select>
      </div>
      
      {/* Deposit Selection */}
      <div style={{ marginBottom: 24 }}>
        <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#1976d2' }}>
          {t('inventory.selectDeposit')}
        </label>
        <select
          value={selectedDeposit || ''}
          onChange={(e) => setSelectedDeposit(Number(e.target.value))}
          disabled={!selectedCompany}
          style={{ 
            padding: '8px 12px', 
            borderRadius: 6, 
            border: '1px solid #1976d2', 
            fontSize: 14,
            minWidth: 200,
            backgroundColor: !selectedCompany ? '#f5f5f5' : 'white',
            cursor: !selectedCompany ? 'not-allowed' : 'pointer'
          }}
        >
          <option value="">
            {!selectedCompany 
              ? t('inventory.selectCompanyFirst') || 'Select a company first...'
              : filteredDeposits.length === 0 
                ? t('inventory.noDepositsForCompany') || 'No deposits for this company'
                : t('inventory.selectDeposit') || 'Select a deposit...'
            }
          </option>
          {filteredDeposits.map(deposit => (
            <option key={deposit.id} value={deposit.id}>
              {deposit.name} {deposit.location && `(${deposit.location})`}
              {deposit.company && ` - ${deposit.company.name}`}
            </option>
          ))}
        </select>
        {selectedCompany && filteredDeposits.length === 0 && (
          <div style={{ marginTop: 8, color: '#f57c00', fontSize: 14, fontStyle: 'italic' }}>
            {t('inventory.noDepositsAssociated') || 'No deposits are associated with the selected company. Please associate deposits with companies in the Personal Cabinet.'}
          </div>
        )}
        {getSelectedDeposit() && (
          <div style={{ marginTop: 8, color: '#666', fontSize: 14 }}>
            {/* <strong>{t('inventory.selectedDepositInfo') || 'Selected:'}</strong> {getSelectedDeposit()?.name} */}
            {getSelectedDeposit()?.company && (
              <span style={{ color: '#1976d2', marginLeft: 8 }}>
                {/* ({getSelectedDeposit()?.company?.name}) */}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Scan Interface */}
      {selectedDeposit && selectedCompany && (
        <>
          <div style={{
            marginBottom: 12, padding: '9px 12px', borderRadius: 9,
            background: lowStockProducts.length ? '#fff7ed' : '#f0fdf4',
            border: `1px solid ${lowStockProducts.length ? '#fb923c' : '#86efac'}`
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setShowLowStockDetails(current => !current)}
                aria-expanded={showLowStockDetails}
                style={{ border: 0, padding: 0, background: 'transparent', cursor: 'pointer', color: lowStockProducts.length ? '#9a3412' : '#166534', fontWeight: 700 }}
              >
                {lowStockProducts.length
                  ? t('inventory.lowStockAlert', { count: lowStockProducts.length })
                  : t('inventory.stockLevelsHealthy')}
                {' '}<span aria-hidden="true">{showLowStockDetails ? '▴' : '▾'}</span>
              </button>
            </div>
            {showLowStockDetails && (
              <div style={{ marginTop: 9, paddingTop: 9, borderTop: '1px solid #fed7aa' }}>
                <label style={{ color: '#475569', fontWeight: 600, fontSize: 13 }}>
                  {t('inventory.lowStockThreshold')}{' '}
                  <input
                    aria-label={t('inventory.lowStockThreshold')}
                    type="number" min="0" max="999" value={lowStockThreshold}
                    onChange={event => {
                      const next = Math.min(999, Math.max(0, Number(event.target.value) || 0));
                      setLowStockThreshold(next);
                      localStorage.setItem('lowStockThreshold', String(next));
                    }}
                    style={{ width: 62, marginLeft: 6, padding: '4px 7px', border: '1px solid #cbd5e1', borderRadius: 6 }}
                  />
                </label>
                {lowStockProducts.length > 0 && <div style={{ marginTop: 8, color: '#7c2d12', fontSize: 13 }}>
                  {lowStockProducts.slice(0, 8).map(dp => `${dp.product.name} (${dp.quantity})`).join(' · ')}
                  {lowStockProducts.length > 8 && ` · ${t('inventory.moreItems', { count: lowStockProducts.length - 8 })}`}
                </div>}
              </div>
            )}
          </div>
          {/* Bulk Mode Controls */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 16, 
            marginBottom: 16,
            padding: 16,
            background: bulkAddMode ? '#e8f5e8' : bulkRemoveMode ? '#ffeaea' : '#f8f9fa',
            borderRadius: 8,
            border: bulkAddMode ? '2px solid #4caf50' : bulkRemoveMode ? '2px solid #f44336' : '1px solid #e0e0e0'
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, marginBottom: 4, color: '#333' }}>
                {t('inventory.scanningMode')}:
              </div>
              <div style={{ fontSize: 14, color: '#666' }}>
                {bulkAddMode ? t('inventory.bulkAddModeActive', { count: bulkCount }) :
                 bulkRemoveMode ? t('inventory.bulkSellModeActive', { count: bulkCount }) :
                 t('inventory.normalMode')}
              </div>
            </div>
            
            <button
              onClick={toggleBulkAddMode}
              style={{
                padding: '10px 20px',
                borderRadius: 6,
                border: 'none',
                background: bulkAddMode ? '#4caf50' : '#e0e0e0',
                color: bulkAddMode ? 'white' : '#333',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                fontSize: 14
              }}
            >
              {bulkAddMode ? t('inventory.exitBulkAdd') : t('inventory.bulkAddMode')}
            </button>
            
            <button
              onClick={toggleBulkRemoveMode}
              style={{
                padding: '10px 20px',
                borderRadius: 6,
                border: 'none',
                background: bulkRemoveMode ? '#f44336' : '#e0e0e0',
                color: bulkRemoveMode ? 'white' : '#333',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                fontSize: 14
              }}
            >
              {bulkRemoveMode ? t('inventory.exitBulkSell') : t('inventory.bulkSellMode')}
            </button>
          </div>

          {/* Bulk Sell Configuration Panel (on-screen) */}
          {bulkRemoveMode && (
            <div style={{
              marginBottom: 16, padding: 20,
              background: 'linear-gradient(135deg, #fff5f5, #ffeaea)',
              borderRadius: 12, border: '2px solid #f44336'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <span style={{ fontSize: 20 }}>🏷️</span>
                <span style={{ fontWeight: 700, fontSize: 16, color: '#c62828' }}>Bulk Sell Configuration</span>
                <span style={{
                  marginLeft: 'auto', background: '#f44336', color: 'white',
                  borderRadius: 20, padding: '2px 12px', fontSize: 13, fontWeight: 700
                }}>
                  {bulkCount} sold
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>

                {/* Sale Percentage */}
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13, color: '#555' }}>
                    Sale Price (% of product price)
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="number"
                      value={salePercentage}
                      onChange={e => setSalePercentage(Math.max(0, Number(e.target.value)))}
                      onFocus={e => e.target.select()}
                      step="5"
                      min="0"
                      max="500"
                      style={{
                        width: 90, padding: '8px 10px', fontSize: 18, fontWeight: 700,
                        border: '2px solid #f44336', borderRadius: 8, outline: 'none',
                        color: '#c62828', textAlign: 'center'
                      }}
                    />
                    <span style={{ fontWeight: 700, color: '#c62828', fontSize: 18 }}>%</span>
                  </div>
                  <div style={{ display: 'flex', gap: 4, marginTop: 8, flexWrap: 'wrap' }}>
                    {[50, 70, 80, 90, 100, 110, 120].map(p => (
                      <button
                        key={p}
                        onClick={() => { setSalePercentage(p); setTimeout(() => scanInputRef.current?.focus(), 50); }}
                        style={{
                          padding: '3px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                          border: `1px solid ${salePercentage === p ? '#f44336' : '#ddd'}`,
                          background: salePercentage === p ? '#f44336' : '#fff',
                          color: salePercentage === p ? 'white' : '#555', fontWeight: 600
                        }}
                      >
                        {p}%
                      </button>
                    ))}
                  </div>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 6, fontStyle: 'italic' }}>
                    100% = full price · 80% = 20% discount · 50% = half price
                  </div>
                </div>

                {/* Delivery Cost (optional) */}
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13, color: '#555' }}>
                    Delivery Cost <span style={{ fontWeight: 400, color: '#aaa' }}>(optional, lei)</span>
                  </label>
                  <input
                    type="number"
                    value={deliveryCost}
                    onChange={e => setDeliveryCost(e.target.value)}
                    onFocus={e => e.target.select()}
                    placeholder="e.g. 25"
                    step="0.01"
                    min="0"
                    style={{
                      width: '100%', padding: '8px 12px', fontSize: 15,
                      border: '1px solid #ddd', borderRadius: 8, outline: 'none',
                      boxSizing: 'border-box', color: '#333'
                    }}
                  />
                  <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>
                    Will be deducted in final report
                  </div>
                </div>

                {/* Ads Cost (optional) */}
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13, color: '#555' }}>
                    Ads / Marketing Cost <span style={{ fontWeight: 400, color: '#aaa' }}>(optional, lei)</span>
                  </label>
                  <input
                    type="number"
                    value={adsCost}
                    onChange={e => setAdsCost(e.target.value)}
                    onFocus={e => e.target.select()}
                    placeholder="e.g. 50"
                    step="0.01"
                    min="0"
                    style={{
                      width: '100%', padding: '8px 12px', fontSize: 15,
                      border: '1px solid #ddd', borderRadius: 8, outline: 'none',
                      boxSizing: 'border-box', color: '#333'
                    }}
                  />
                  <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>
                    Will be deducted in final report
                  </div>
                </div>
              </div>

              {/* Live preview of last sold item */}
              {bulkSellItems.length > 0 && (() => {
                const last = bulkSellItems[bulkSellItems.length - 1];
                return (
                  <div style={{
                    marginTop: 14, padding: '8px 14px', background: 'rgba(244,67,54,0.08)',
                    borderRadius: 8, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13
                  }}>
                    <span style={{ color: '#f44336' }}>✓</span>
                    <span style={{ fontWeight: 600 }}>Last sold:</span>
                    <span>{last.productName}</span>
                    <span style={{ marginLeft: 'auto', fontWeight: 700, color: '#f44336' }}>
                      {last.price.toFixed(2)} lei
                      <span style={{ fontWeight: 400, color: '#888', marginLeft: 6 }}>
                        ({last.percentage}% of {last.originalPrice})
                      </span>
                    </span>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Bulk Sell Statistics Modal */}
          {showBulkSellStats && (() => {
            const totalRevenue = bulkSellItems.reduce((s, i) => s + i.price, 0);
            const totalOriginal = bulkSellItems.reduce((s, i) => s + i.originalPrice, 0);
            const delivery = parseFloat(deliveryCost) || 0;
            const ads = parseFloat(adsCost) || 0;
            const netProfit = totalRevenue - delivery - ads;
            const avgPrice = bulkSellItems.length > 0 ? totalRevenue / bulkSellItems.length : 0;
            const avgDiscount = bulkSellItems.length > 0
              ? bulkSellItems.reduce((s, i) => s + i.percentage, 0) / bulkSellItems.length
              : 0;
            // Group by the stable internal key, while displaying only business details.
            // Product names are not unique across model and size variants.
            const grouped = bulkSellItems.reduce((acc, item) => {
              const key = String(item.productId);
              if (!acc[key]) acc[key] = { productId: item.productId, productName: item.productName, count: 0, total: 0, avgPct: 0, pcts: [] };
              acc[key].count++;
              acc[key].total += item.price;
              acc[key].pcts.push(item.percentage);
              return acc;
            }, {} as Record<string, { productId: number; productName: string; count: number; total: number; avgPct: number; pcts: number[] }>);
            const groupedList = Object.values(grouped).map(g => ({
              ...g,
              avgPct: g.pcts.reduce((a, b) => a + b, 0) / g.pcts.length
            }));

            return (
              <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center',
                justifyContent: 'center', zIndex: 1300
              }}>
                <div style={{
                  background: 'white', borderRadius: 20, width: '95%', maxWidth: 640,
                  maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
                  boxShadow: '0 24px 80px rgba(0,0,0,0.4)'
                }}>
                  {/* Gradient header */}
                  <div style={{ background: 'linear-gradient(135deg, #c62828, #f44336)', padding: '24px 28px', color: 'white' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
                      <span style={{ fontSize: 38 }}>🛒</span>
                      <div>
                        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>Bulk Sell Session Complete</h2>
                        <p style={{ margin: '4px 0 0', opacity: 0.8, fontSize: 13 }}>Session summary & financials</p>
                      </div>
                    </div>
                    {/* Top stats row */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                      {[
                        { value: bulkSellItems.length.toString(), label: 'Items Sold' },
                        { value: totalRevenue.toFixed(2) + ' lei', label: 'Gross Revenue' },
                        { value: avgPrice.toFixed(2) + ' lei', label: 'Avg Price' },
                        { value: avgDiscount.toFixed(0) + '%', label: 'Avg % Applied' },
                      ].map(card => (
                        <div key={card.label} style={{
                          background: 'rgba(255,255,255,0.15)', borderRadius: 10,
                          padding: '10px 14px', backdropFilter: 'blur(4px)'
                        }}>
                          <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.2 }}>{card.value}</div>
                          <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2 }}>{card.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Scrollable content */}
                  <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px' }}>

                    {/* Financial Summary */}
                    <div style={{
                      background: '#f8f9fa', borderRadius: 12, padding: 16, marginBottom: 20,
                      border: '1px solid #e9ecef'
                    }}>
                      <h4 style={{ margin: '0 0 14px', color: '#333', fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        💰 Financial Summary
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                          <span style={{ color: '#555' }}>Gross Revenue</span>
                          <span style={{ fontWeight: 700, color: '#1a1a1a' }}>{totalRevenue.toFixed(2)} lei</span>
                        </div>
                        {totalOriginal > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                            <span style={{ color: '#888' }}>Original price total</span>
                            <span style={{ color: '#888' }}>{totalOriginal.toFixed(2)} lei</span>
                          </div>
                        )}
                        {delivery > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                            <span style={{ color: '#e65100' }}>− Delivery Cost</span>
                            <span style={{ fontWeight: 600, color: '#e65100' }}>−{delivery.toFixed(2)} lei</span>
                          </div>
                        )}
                        {ads > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                            <span style={{ color: '#6a1b9a' }}>− Ads / Marketing</span>
                            <span style={{ fontWeight: 600, color: '#6a1b9a' }}>−{ads.toFixed(2)} lei</span>
                          </div>
                        )}
                        <div style={{ borderTop: '2px solid #dee2e6', marginTop: 4, paddingTop: 10, display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontWeight: 700, fontSize: 16, color: netProfit >= 0 ? '#2e7d32' : '#c62828' }}>Net Profit</span>
                          <span style={{ fontWeight: 800, fontSize: 18, color: netProfit >= 0 ? '#2e7d32' : '#c62828' }}>
                            {netProfit >= 0 ? '' : '−'}{Math.abs(netProfit).toFixed(2)} lei
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Product Summary (grouped) */}
                    {groupedList.length > 0 && (
                      <div style={{ marginBottom: 20 }}>
                        <h4 style={{ margin: '0 0 10px', color: '#333', fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          📦 Products Summary
                        </h4>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                          <thead>
                            <tr style={{ background: '#f1f3f5', borderBottom: '2px solid #dee2e6' }}>
                              <th style={{ padding: '8px 10px', textAlign: 'left', color: '#495057', fontWeight: 600 }}>Product</th>
                              <th style={{ padding: '8px 10px', textAlign: 'center', color: '#495057', fontWeight: 600 }}>Qty</th>
                              <th style={{ padding: '8px 10px', textAlign: 'center', color: '#495057', fontWeight: 600 }}>Avg %</th>
                              <th style={{ padding: '8px 10px', textAlign: 'right', color: '#495057', fontWeight: 600 }}>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {groupedList.sort((a, b) => b.total - a.total).map((g, idx) => (
                              <tr key={g.productId} style={{ borderBottom: '1px solid #f0f0f0', background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                                <td style={{ padding: '8px 10px', fontWeight: 500, color: '#212529' }}>{g.productName}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600 }}>{g.count}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'center', color: '#f44336', fontWeight: 600 }}>{g.avgPct.toFixed(0)}%</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: '#1a1a1a' }}>{g.total.toFixed(2)} lei</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Full Scan Log */}
                    <details>
                      <summary style={{
                        cursor: 'pointer', fontWeight: 700, fontSize: 13, color: '#666',
                        textTransform: 'uppercase', letterSpacing: 0.5, userSelect: 'none',
                        marginBottom: 8, padding: '6px 0'
                      }}>
                        📋 Full Scan Log ({bulkSellItems.length} items)
                      </summary>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 8 }}>
                        <thead>
                          <tr style={{ background: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
                            <th style={{ padding: '6px 8px', textAlign: 'left', color: '#6c757d' }}>#</th>
                            <th style={{ padding: '6px 8px', textAlign: 'left', color: '#6c757d' }}>Product</th>
                            <th style={{ padding: '6px 8px', textAlign: 'center', color: '#6c757d' }}>%</th>
                            <th style={{ padding: '6px 8px', textAlign: 'right', color: '#6c757d' }}>Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bulkSellItems.map((item, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px solid #f5f5f5' }}>
                              <td style={{ padding: '5px 8px', color: '#aaa' }}>{idx + 1}</td>
                              <td style={{ padding: '5px 8px', color: '#333' }}>{item.productName}</td>
                              <td style={{ padding: '5px 8px', textAlign: 'center', color: '#f44336', fontWeight: 600 }}>{item.percentage}%</td>
                              <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 600, color: '#212529' }}>{item.price.toFixed(2)} lei</td>
                            </tr>
                          ))}
                          <tr style={{ background: '#fff3e0', borderTop: '2px solid #ffe0b2' }}>
                            <td colSpan={3} style={{ padding: '8px', fontWeight: 700, color: '#e65100', fontSize: 13 }}>TOTAL</td>
                            <td style={{ padding: '8px', textAlign: 'right', fontWeight: 800, fontSize: 15, color: '#e65100' }}>
                              {totalRevenue.toFixed(2)} lei
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </details>
                  </div>

                  {/* Footer */}
                  <div style={{ padding: '16px 28px', borderTop: '1px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                    <button
                      onClick={() => { setShowBulkSellStats(false); setBulkSellItems([]); }}
                      style={{
                        padding: '10px 32px', background: '#1976d2', color: 'white',
                        border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 15, fontWeight: 700
                      }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}

          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 12, 
            flexWrap: 'wrap',
            marginBottom: 18, 
            background: '#f5f8fa', 
            borderRadius: 8, 
            padding: 16, 
            boxShadow: '0 1px 4px #0001' 
          }}>
          <input
            type="text"
            ref={scanInputRef}
            autoFocus
            placeholder={
              bulkAddMode ? t('inventory.bulkAddPlaceholder') :
              bulkRemoveMode ? t('inventory.bulkRemovePlaceholder') :
              t('inventory.scanPlaceholder')
            }
            value={scanValue}
            onChange={e => setScanValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                if (bulkAddMode || (!bulkRemoveMode && !e.shiftKey)) {
                  handleScanAdd();
                } else if (bulkRemoveMode || e.shiftKey) {
                  handleScanRemove();
                }
              }
            }}
            style={{ 
              padding: 12, 
              borderRadius: 6, 
              border: bulkAddMode ? '2px solid #4caf50' : bulkRemoveMode ? '2px solid #f44336' : '1px solid #1976d2', 
              width: 300,
              fontSize: 14,
              background: bulkAddMode ? '#f0fff0' : bulkRemoveMode ? '#fff5f5' : 'white'
            }}
            disabled={loading}
          />
          {!bulkRemoveMode && (
            <button
              onClick={() => handleScanAdd()}
              title={bulkAddMode ? t('inventory.bulkAddSelected') : t('inventory.addToInventory')}
              disabled={loading}
              style={{
                background: bulkAddMode ? '#4caf50' : '#1976d2',
                border: 'none',
                borderRadius: '50%',
                padding: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 7v10M7 12h10" stroke="white" strokeWidth="2.2" strokeLinecap="round"/>
              </svg>
            </button>
          )}
          {!bulkAddMode && (
            <button
              onClick={() => handleScanRemove()}
              title={bulkRemoveMode ? t('inventory.bulkSellSelected') : t('inventory.removeFromInventory')}
              disabled={loading}
              style={{
                background: '#f44336',
                border: 'none',
                borderRadius: '50%',
                padding: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
                fontSize: 18,
                fontWeight: 'bold',
                color: 'white',
                width: 40,
                height: 40
              }}
            >
              −
            </button>
          )}
          {loading && <span style={{ color: '#1976d2' }}>{t('inventory.processing')}</span>}
          <details
            open={manualEntryOpen}
            onToggle={event => setManualEntryOpen(event.currentTarget.open)}
            style={{ flexBasis: '100%', borderTop: '1px solid #dbe5ef', paddingTop: 10 }}
          >
            <summary style={{ cursor: 'pointer', color: '#475569', fontSize: 14, fontWeight: 600 }}>
              {bulkAddMode
                ? t('inventory.manualBulkAddToggle')
                : bulkRemoveMode
                  ? t('inventory.manualBulkSellToggle')
                  : t('inventory.manualEntryToggle')}
            </summary>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', paddingTop: 12 }}>
              <select
                aria-label={t('inventory.manualProduct')}
                value={manualProductId ?? ''}
                onChange={event => {
                  setManualProductId(event.target.value ? Number(event.target.value) : null);
                  setError('');
                  setSuccess('');
                }}
                disabled={loading}
                style={{
                  padding: 12,
                  borderRadius: 6,
                  border: '1px solid #94a3b8',
                  minWidth: 280,
                  maxWidth: '100%',
                  fontSize: 14,
                  background: 'white'
                }}
              >
                <option value="">{t('inventory.manualProduct')}</option>
                {products.filter(product => product.id != null).map(product => (
                  <option key={product.id} value={product.id}>
                    {getProductDisplayName(product)}
                  </option>
                ))}
              </select>
              {!bulkRemoveMode && (
                <button
                  type="button"
                  onClick={() => handleScanAdd(manuallySelectedProduct)}
                  disabled={loading || !manuallySelectedProduct}
                  title={bulkAddMode ? t('inventory.bulkAddSelected') : t('inventory.addToInventory')}
                  style={{
                    border: 'none', borderRadius: 6, padding: '10px 16px', color: 'white',
                    background: bulkAddMode ? '#4caf50' : '#1976d2', fontWeight: 700,
                    cursor: loading || !manuallySelectedProduct ? 'not-allowed' : 'pointer',
                    opacity: loading || !manuallySelectedProduct ? 0.55 : 1
                  }}
                >
                  + {bulkAddMode ? t('inventory.bulkAddSelected') : t('inventory.addToInventory')}
                </button>
              )}
              {!bulkAddMode && (
                <button
                  type="button"
                  onClick={() => handleScanRemove(manuallySelectedProduct)}
                  disabled={loading || !manuallySelectedProduct}
                  title={bulkRemoveMode ? t('inventory.bulkSellSelected') : t('inventory.removeFromInventory')}
                  style={{
                    border: 'none', borderRadius: 6, padding: '10px 16px', color: 'white',
                    background: '#f44336', fontWeight: 700,
                    cursor: loading || !manuallySelectedProduct ? 'not-allowed' : 'pointer',
                    opacity: loading || !manuallySelectedProduct ? 0.55 : 1
                  }}
                >
                  − {bulkRemoveMode ? t('inventory.bulkSellSelected') : t('inventory.removeFromInventory')}
                </button>
              )}
              <div style={{ flexBasis: '100%', color: '#64748b', fontSize: 13 }}>
                {bulkAddMode
                  ? t('inventory.manualBulkAddHelp')
                  : bulkRemoveMode
                    ? t('inventory.manualBulkSellHelp', { percentage: salePercentage })
                    : t('inventory.manualProductHelp')}
              </div>
            </div>
          </details>
        </div>

      {/* Messages */}
      {error && (
        <div style={{ 
          marginBottom: 16, 
          padding: '12px 16px', 
          background: '#f5f5f5', 
          color: '#000', 
          borderRadius: 6,
          border: '1px solid #ffcdd2'
        }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{ 
          marginBottom: 16, 
          padding: '12px 16px', 
          background: '#e3f2fd', 
          color: '#1976d2', 
          borderRadius: 6,
          border: '1px solid #c8e6c9'
        }}>
          {success}
        </div>
      )}

      {/* Inventory Table */}
      {selectedDeposit && (
        <div>
          <h3 style={{ color: '#1976d2', marginBottom: 16 }}>
            {t('inventory.inventoryIn', { depositName: getSelectedDeposit()?.name })}
          </h3>
          
          {/* Filter Section */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '16px', 
            marginBottom: '20px',
            padding: '16px',
            background: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e9ecef'
          }}>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: '#495057' }}>
                {t('common.search') || 'Search'}
              </label>
              <input
                type="text"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                placeholder={t('inventory.searchProducts') || 'Search products...'}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: '#495057' }}>
                {t('inventory.firm')}
              </label>
              <input
                type="text"
                list="inventory-firm-suggestions"
                value={firmFilter}
                onChange={(e) => setFirmFilter(e.target.value)}
                placeholder={t('inventory.allFirms')}
                autoComplete="off"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
              <datalist id="inventory-firm-suggestions">{uniqueFirms.map(firm => <option key={firm} value={firm} />)}</datalist>
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: '#495057' }}>
                {t('inventory.model')}
              </label>
              <input
                type="text"
                list="inventory-model-suggestions"
                value={modelFilter}
                onChange={(e) => setModelFilter(e.target.value)}
                placeholder={t('inventory.allModels')}
                autoComplete="off"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
              <datalist id="inventory-model-suggestions">{uniqueModels.map(model => <option key={model} value={model} />)}</datalist>
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', color: '#495057' }}>
                {t('inventory.size')}
              </label>
              <input
                type="text"
                list="inventory-size-suggestions"
                value={sizeFilter}
                onChange={(e) => setSizeFilter(e.target.value)}
                placeholder={t('inventory.allSizes')}
                autoComplete="off"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
              <datalist id="inventory-size-suggestions">{uniqueSizes.map(size => <option key={size} value={size} />)}</datalist>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'end' }}>
              <button
                onClick={() => {
                  setSearchFilter('');
                  setFirmFilter('');
                  setModelFilter('');
                  setSizeFilter('');
                }}
                style={{
                  padding: '8px 16px',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                {t('common.clearFilters') || 'Clear Filters'}
              </button>
            </div>
          </div>
          
          <table style={{ 
            width: '100%', 
            borderCollapse: 'separate', 
            borderSpacing: 0, 
            background: '#f5f8fa', 
            borderRadius: 8, 
            boxShadow: '0 1px 4px #0001' 
          }}>
            <thead>
              <tr style={{ background: '#1976d2', color: '#fff' }}>
                <th style={{ padding: '12px 8px', textAlign: 'left' }}>{t('inventory.product')}</th>
                <th style={{ padding: '12px 8px', textAlign: 'center' }}>{t('inventory.size')}</th>
                <th style={{ padding: '12px 8px', textAlign: 'center' }}>{t('inventory.firm')}</th>
                <th style={{ padding: '12px 8px', textAlign: 'center' }}>{t('inventory.model')}</th>
                <th style={{ padding: '12px 8px', textAlign: 'center' }}>{t('inventory.quantity')}</th>
                <th style={{ padding: '12px 8px', textAlign: 'center' }}>{t('inventory.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredDepositProducts.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: '24px', textAlign: 'center', color: '#666' }}>
                    {depositProducts.length === 0 
                      ? t('inventory.noProductsInDeposit')
                      : (t('inventory.noProductsMatchFilter') || 'No products match the current filter')
                    }
                  </td>
                </tr>
              ) : (
                filteredDepositProducts.map((dp, idx) => (
                  <tr key={dp.id} style={{ background: idx % 2 === 0 ? '#fff' : '#f0f4fa' }}>
                    <td style={{ padding: '12px 8px' }}>
                      <div>
                        <strong>{dp.product.name || t('inventory.unknownProduct')}</strong>
                        <div style={{ fontSize: 12, color: '#666' }}>
                          {dp.product.description || t('inventory.noDescription')}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '12px 8px', textAlign: 'center' }}>{dp.product.size}</td>
                    <td style={{ padding: '12px 8px', textAlign: 'center' }}>{dp.product.firm}</td>
                    <td style={{ padding: '12px 8px', textAlign: 'center' }}>{dp.product.model}</td>
                    <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                        <button
                          onClick={() => handleQuantityChange(dp, dp.quantity - 1)}
                          disabled={loading || dp.quantity <= 0}
                          style={{
                            background: '#f44336',
                            border: 'none',
                            borderRadius: '50%',
                            width: 24,
                            height: 24,
                            color: 'white',
                            cursor: loading || dp.quantity <= 0 ? 'not-allowed' : 'pointer',
                            opacity: loading || dp.quantity <= 0 ? 0.5 : 1
                          }}
                        >
                          -
                        </button>
                        <span style={{ minWidth: 30, textAlign: 'center', fontWeight: 600 }}>
                          {dp.quantity}
                        </span>
                        <button
                          onClick={() => handleQuantityChange(dp, dp.quantity + 1)}
                          disabled={loading}
                          style={{
                            background: '#19d251ff',
                            border: 'none',
                            borderRadius: '50%',
                            width: 24,
                            height: 24,
                            color: 'white',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            opacity: loading ? 0.5 : 1
                          }}
                        >
                          +
                        </button>
                      </div>
                    </td>
                    <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                      <button
                        onClick={() => handleRemoveFromDeposit(dp)}
                        disabled={loading}
                        title="Remove all from deposit"
                        style={{
                          background: '#f44336',
                          border: 'none',
                          borderRadius: '50%',
                          width: 28,
                          height: 28,
                          color: 'white',
                          cursor: loading ? 'not-allowed' : 'pointer',
                          fontSize: 16,
                          fontWeight: 'bold',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: loading ? 0.5 : 1
                        }}
                      >
                        &#x1F5D1;
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
        </>
      )}
    </div>
  );
}
